import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { Drop, Stream } from '@/types';
import { FiVideo, FiPackage, FiArrowRight, FiChevronRight, FiGlobe, FiLock, FiUsers } from 'react-icons/fi';
import DropCard from '@/components/drops/DropCard';
import StreamCard from '@/components/streams/StreamCard';
import NewHeroSection from '@/components/home/NewHeroSection';
import FeaturedCreators from '@/components/home/FeaturedCreators';
import { motion } from 'framer-motion';
import { useDemoMode } from '@/context/DemoModeContext';

const NewHomePage = () => {
  const [activeTab, setActiveTab] = useState<'drops' | 'streams'>('drops');
  const { isDemoMode } = useDemoMode();

  // Fetch featured drops
  const { data: dropsData, isLoading: isLoadingDrops } = useQuery({
    queryKey: ['featuredDrops', isDemoMode],
    queryFn: async () => {
      // Always use demo data for now since the API is returning 500 errors
      const { demoDrops } = await import('@/services/demoData');
      
      if (isDemoMode) {
        console.log('Using demo drops data (Demo Mode)');
        return demoDrops;
      }
      
      try {
        const response = await api.get<Drop[]>('/api/drops?featured=true&limit=6');
        if (response.data && response.data.data && response.data.data.length > 0) {
          return response.data.data;
        } else {
          console.log('API returned empty data, falling back to demo drops data');
          return demoDrops;
        }
      } catch (error) {
        console.log('API error, falling back to demo drops data', error);
        return demoDrops;
      }
    },
    retry: false
  });

  // Fetch live streams
  const { data: streamsData, isLoading: isLoadingStreams } = useQuery({
    queryKey: ['liveStreams', isDemoMode],
    queryFn: async () => {
      // Always use demo data for now since the API is returning 500 errors
      const { demoStreams } = await import('@/services/demoData');
      
      // Convert demo streams to match Stream type
      const formattedDemoStreams = demoStreams.map(stream => ({
        ...stream,
        isTokenGated: false,
        updatedAt: stream.createdAt,
      })) as Stream[];
      
      if (isDemoMode) {
        console.log('Using demo streams data (Demo Mode)');
        return formattedDemoStreams;
      }
      
      try {
        const response = await api.get<Stream[]>('/api/streams?isActive=true&limit=6');
        if (response.data && response.data.data && response.data.data.length > 0) {
          return response.data.data;
        } else {
          console.log('API returned empty data, falling back to demo streams data');
          return formattedDemoStreams;
        }
      } catch (error) {
        console.log('API error, falling back to demo streams data', error);
        return formattedDemoStreams;
      }
    },
    retry: false
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <NewHeroSection />

      {/* Featured Content */}
      <section className="section bg-gradient-to-b from-dark-900 to-dark-800 relative">
        {/* Background elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary-500/5 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary-500/5 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Featured Content</h2>
              <p className="text-gray-400 max-w-xl">
                Discover the latest drops and live streams from top creators
              </p>
            </div>
            
            <div className="mt-4 md:mt-0 glass-card p-1 rounded-xl shadow-lg">
              <div className="flex space-x-1">
                <TabButton 
                  isActive={activeTab === 'drops'} 
                  onClick={() => setActiveTab('drops')}
                  icon={<FiPackage />}
                  label="Drops"
                />
                <TabButton 
                  isActive={activeTab === 'streams'} 
                  onClick={() => setActiveTab('streams')}
                  icon={<FiVideo />}
                  label="Live"
                />
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeTab === 'drops' ? (
              isLoadingDrops ? (
                // Loading skeleton for drops
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="card animate-pulse h-80"></div>
                ))
              ) : dropsData && dropsData.length > 0 ? (
                dropsData.map((drop: Drop) => <DropCard key={drop.id} drop={drop} />)
              ) : (
                <div className="col-span-3 text-center py-12 card">
                  <FiPackage size={48} className="mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400 text-lg">No featured drops available</p>
                </div>
              )
            ) : isLoadingStreams ? (
              // Loading skeleton for streams
              Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="card animate-pulse h-80"></div>
              ))
            ) : streamsData && streamsData.length > 0 ? (
              streamsData.map((stream: Stream) => <StreamCard key={stream.id} stream={stream} />)
            ) : (
              <div className="col-span-3 text-center py-12 card">
                <FiVideo size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400 text-lg">No live streams available</p>
              </div>
            )}
          </div>

          {/* View All Button */}
          <div className="mt-12 text-center">
            <Link
              to={activeTab === 'drops' ? '/explore' : '/streams'}
              className="btn-primary btn-lg group"
            >
              <span>View All {activeTab === 'drops' ? 'Drops' : 'Streams'}</span>
              <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Creators */}
      <FeaturedCreators />

      {/* Platform Features */}
      <section className="section bg-dark-800 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold heading-gradient inline-block mb-4">Platform Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              FTTF.World combines decentralized streaming, token-gated content, and NFT marketplace in one seamless platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<FiVideo />}
              title="Decentralized Streaming"
              description="Stream content powered by Livepeer's decentralized network, ensuring high quality and low costs."
              color="primary"
              delay={0.1}
            />
            
            <FeatureCard 
              icon={<FiLock />}
              title="Token-Gated Content"
              description="Create exclusive content accessible only to token holders, enabling new monetization models."
              color="secondary"
              delay={0.2}
            />
            
            <FeatureCard 
              icon={<FiGlobe />}
              title="NFT Marketplace"
              description="Mint, buy, and sell NFTs with built-in royalties, ensuring creators earn from secondary sales."
              color="accent"
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section bg-dark-900 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-[url('https://raw.githubusercontent.com/shadcn-ui/ui/main/apps/www/public/grid.svg')] bg-center opacity-5 z-0"></div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Get started with FTTF.World in three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-24 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 z-0"></div>
            
            {/* Step 1 */}
            <StepCard 
              number={1}
              title="Connect Your Wallet"
              description="Link your Ethereum wallet to access the platform and manage your digital assets."
              delay={0.1}
            />
            
            {/* Step 2 */}
            <StepCard 
              number={2}
              title="Explore Content"
              description="Discover exclusive drops, live streams, and NFTs from your favorite creators."
              delay={0.2}
            />
            
            {/* Step 3 */}
            <StepCard 
              number={3}
              title="Collect & Support"
              description="Purchase NFTs, unlock token-gated content, and tip creators directly."
              delay={0.3}
            />
          </div>
          
          <div className="mt-16 text-center">
            <Link to="/about" className="btn-outline btn-lg group">
              <span>Learn More</span>
              <FiChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Community Section */}
      <section className="section bg-dark-800 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"></div>
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary-500/5 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-secondary-500/5 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Join Our Community</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Connect with other creators and fans in our growing ecosystem
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <CommunityCard 
              icon={<FiUsers />}
              title="Creator Community"
              description="Join our Discord server to connect with other creators, share tips, and collaborate on projects."
              buttonText="Join Discord"
              buttonLink="https://discord.gg/fttf-world"
              delay={0.1}
            />
            
            <CommunityCard 
              icon={<FiGlobe />}
              title="Governance Forum"
              description="Participate in platform governance, vote on proposals, and help shape the future of FTTF.World."
              buttonText="Visit Forum"
              buttonLink="/governance"
              delay={0.2}
            />
            
            <CommunityCard 
              icon={<FiVideo />}
              title="Creator Academy"
              description="Access tutorials, workshops, and resources to help you create better content and grow your audience."
              buttonText="Learn More"
              buttonLink="/academy"
              delay={0.3}
            />
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900/30 to-secondary-900/30 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-dark-900/80"></div>
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary-500/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-secondary-500/10 to-transparent"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="glass-card p-8 md:p-12 text-center max-w-4xl mx-auto shadow-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Join the Revolution?</h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Connect your wallet today and start exploring the future of decentralized media and NFTs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/explore" className="btn-primary btn-lg group">
                <span>Start Exploring</span>
                <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/about" className="btn-outline">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Tab Button Component
const TabButton = ({ isActive, onClick, icon, label }: { isActive: boolean; onClick: () => void; icon: React.ReactNode; label: string }) => {
  return (
    <motion.button
      className={`px-4 py-2.5 rounded-lg transition-colors flex items-center space-x-2 ${
        isActive
          ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md'
          : 'text-gray-400 hover:text-white hover:bg-dark-700'
      }`}
      onClick={onClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
    >
      {icon}
      <span>{label}</span>
    </motion.button>
  );
};

// Feature Card Component
const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  color = 'primary',
  delay = 0
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  color?: 'primary' | 'secondary' | 'accent';
  delay?: number;
}) => {
  const colorClasses = {
    primary: 'text-primary-500 bg-primary-500/10 border-primary-500/20',
    secondary: 'text-secondary-500 bg-secondary-500/10 border-secondary-500/20',
    accent: 'text-accent-500 bg-accent-500/10 border-accent-500/20'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="glass-card p-6 text-center shadow-3d transition-all duration-300 border border-dark-700 hover:border-primary-500/30 hover:shadow-3d-hover"
      whileHover={{ y: -8 }}
    >
      <div className={`w-14 h-14 rounded-xl ${colorClasses[color]} flex items-center justify-center mx-auto mb-4 text-2xl`}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-white mb-3 text-shadow">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </motion.div>
  );
};

// Step Card Component
const StepCard = ({ 
  number, 
  title, 
  description,
  delay = 0
}: { 
  number: number; 
  title: string; 
  description: string;
  delay?: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="glass-card p-6 text-center relative z-10 border border-dark-700 shadow-3d transition-all duration-300 hover:border-primary-500/30 hover:shadow-3d-hover"
      whileHover={{ y: -8 }}
    >
      <div className="w-14 h-14 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl shadow-3d">
        {number}
      </div>
      <h3 className="text-xl font-bold text-white mb-3 text-shadow">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </motion.div>
  );
};

// Community Card Component
const CommunityCard = ({ 
  icon, 
  title, 
  description, 
  buttonText,
  buttonLink,
  delay = 0
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  buttonText: string;
  buttonLink: string;
  delay?: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="glass-card p-6 text-center border border-dark-700 shadow-3d transition-all duration-300 hover:border-primary-500/30 hover:shadow-3d-hover flex flex-col h-full"
      whileHover={{ y: -8 }}
    >
      <motion.div 
        className="w-14 h-14 rounded-xl bg-dark-700 text-primary-500 flex items-center justify-center mx-auto mb-4 text-2xl shadow-md"
        whileHover={{ scale: 1.1 }}
        transition={{ duration: 0.2 }}
      >
        {icon}
      </motion.div>
      <h3 className="text-xl font-bold text-white mb-3 text-shadow">{title}</h3>
      <p className="text-gray-400 mb-6 flex-grow">{description}</p>
      <Link to={buttonLink} className="btn-outline w-full">
        {buttonText}
      </Link>
    </motion.div>
  );
};

export default NewHomePage;